import type { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { fuzzyMatch } from '../../common/functions/fuzzy-match';
import {
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxRoot,
} from '../fields/combobox/combobox';
import ErrorLabel from '../labels/error-label';

export interface SchemaSelectItem {
  value: string;
  label: string;
  /** A secondary line under the name. Not searched. */
  description?: string;
}

export interface SchemaComboboxProps
  extends Omit<ComponentProps<typeof ComboboxInput>, 'value' | 'onValueChange' | 'children'> {
  schemas: SchemaSelectItem[];
  value: SchemaSelectItem | null;
  onValueChange: (value: SchemaSelectItem | null) => void;
  isLoading?: boolean;
  error?: string;
}

export const SchemaCombobox = ({
  schemas,
  value,
  onValueChange,
  isLoading = false,
  error,
  ...inputProps
}: SchemaComboboxProps) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col">
      <ComboboxRoot
        items={schemas}
        value={value}
        onValueChange={onValueChange}
        itemToStringLabel={(schema) => schema.label}
        isItemEqualToValue={(item, selected) => item.value === selected.value}
        filter={(schema, query) => fuzzyMatch(schema.label, query)}
      >
        <ComboboxInput aria-invalid={error !== undefined || undefined} {...inputProps} />
        <ComboboxContent>
          <ComboboxEmpty>
            {isLoading && schemas.length === 0 ? t(($) => $.comboboxLoading) : t(($) => $.comboboxEmpty)}
          </ComboboxEmpty>
          <ComboboxList>
            {(schema: SchemaSelectItem) => (
              <ComboboxItem key={schema.value} value={schema} title={schema.label}>
                <div className="flex min-w-0 flex-col">
                  <div className="truncate">{schema.label}</div>
                  {schema.description && (
                    <div className="truncate text-muted-foreground text-xs">{schema.description}</div>
                  )}
                </div>
              </ComboboxItem>
            )}
          </ComboboxList>
        </ComboboxContent>
      </ComboboxRoot>
      {error && <ErrorLabel>{error}</ErrorLabel>}
    </div>
  );
};
